import LineIcon from "@assets/images/line.svg";
import {
  Box,
  FormControlLabel,
  Grid,
  GridSize,
  InputProps,
  Radio,
  RadioGroup,
  SelectProps
} from "@material-ui/core";
import InputSuggestion from "@saleor/alternatedMaterial/components/InputSuggestionForPrioridy";
import OrderStatus, {
  IChoice
} from "@saleor/changeOrderExport/components/OrderStatus/OrderStatus";
import AutoSuggestionMultipleSelect from "@saleor/components/AutoSuggestionMutipleSelect";
import DatePicker from "@saleor/components/DatePicker/DatePickerV2";
import MyTypography from "@saleor/components/MyTypography";
import SingleSelectField, {
  Choice
} from "@saleor/components/SingleSelectField";
import { SingleTextField } from "@saleor/components/SingleTextField";
import TextInputDisable from "@saleor/components/TextInputDisable";
import { PageInfo } from "@saleor/sdk/dist/apollo/types";
import classNames from "classnames";
import moment from "moment-timezone";
import React from "react";
import SVG from "react-inlinesvg";

import NumberInput from "../NumberInput";
import { FIELD_TYPE } from "./constants";
import useStyles from "./styles";

interface Props {
  type: string;
  name: string;
  value: any;
  placeholder?: string;
  handleChange?: (event: any) => void;
  customOnChange?: (event: any) => void;
  error?: any;
  maxLength?: number;
  options?: Choice[];
  multiSelectOptions?: IChoice[];
  isClean?: boolean;
  handleOpenShipTo?: () => void;
  isShowSelected?: boolean;
  reRender?: boolean;
  headerText?: string;
  message?: string;
  keyFilterApi?: string;
  md?: number;
  sm?: number;
  isShipTo?: boolean;
  title?: string;
  required?: boolean;
  iconRight?: any;
  disableManualDateInput?: boolean;
  dateFormat?: string;
  dateRangeInDays?: number;
  isAllowTodayDate?: boolean;
  disableFutureDate?: boolean;
  optionsShowCircularIcon?: boolean;
  mappingDataSuggestions?: {
    fetchDataSuggestions: any;
    loadingDataSuggestion: boolean;
    getDataSuggestions: any;
    searchField?: string;
    dataOptions: Array<{ title: string; value: any }> | [];
    isCheckingRole?: boolean;
    totalCount?: number;
    pageInfo?: PageInfo;
    showLoadMore?: boolean;
  };
  style?: React.CSSProperties;
  className?: string;
  InputProps?: InputProps;
  selectProps?: SelectProps;
  gridStyle?: React.CSSProperties;
  isUpdateDate?: boolean;
  maxDate?: any;
  minDate?: any;
  isDisabled?: boolean;
  labelParser?: (value: any) => any;
  hint?: any;
  nonBold?: boolean;
  titleInfo?: string;
  isSearchIcon?: boolean;
  displayText?: string;
  warningMessage?: string;
  id?: string;
}

const FormField = ({
  type,
  name,
  value,
  placeholder,
  handleChange,
  customOnChange,
  error,
  maxLength,
  options,
  multiSelectOptions,
  isClean,
  handleOpenShipTo,
  isShowSelected,
  reRender,
  headerText,
  message,
  keyFilterApi,
  md,
  sm,
  isShipTo,
  title,
  required,
  iconRight,
  mappingDataSuggestions,
  disableManualDateInput = true,
  dateFormat = "YYYY-MM-DD",
  dateRangeInDays = 30,
  disableFutureDate = true,
  isAllowTodayDate = true,
  optionsShowCircularIcon = false,
  style,
  className,
  selectProps,
  InputProps,
  gridStyle,
  isUpdateDate = false,
  minDate,
  maxDate,
  isDisabled,
  labelParser,
  hint,
  nonBold,
  titleInfo,
  isSearchIcon = false,
  displayText,
  warningMessage,
  id
}: Props) => {
  const classes = useStyles();
  const commonProps = {
    name,
    value,
    error: !!error,
    placeholder,
    style,
    className
  };

  const getTextError = () => {
    if (!error?.lte && !error?.gte) {
      return <div className={classes.textError}>{error}</div>;
    }
  };

  const getTextWarning = () =>
    warningMessage &&
    !error && <div className={classes.textError}>{warningMessage}</div>;

  const getTextErrorDatePick = () => {
    if (error?.lte) {
      return <div className={classes.textError}>{error?.lte}</div>;
    }
    if (error?.gte) {
      return <div className={classes.textError}>{error?.gte}</div>;
    }
  };

  const renderChild = (children: JSX.Element) => (
    <Grid
      item
      md={md ? (md as GridSize) : 2}
      sm={sm ? (sm as GridSize) : 6}
      style={gridStyle}
    >
      <div
        className={classNames({
          [classes.datePicker]: type === FIELD_TYPE.DATERANGE_PICKER
        })}
        style={{ position: "relative" }}
      >
        <MyTypography
          variant={nonBold ? "body3" : "subtitle3"}
          className={classNames({
            [classes.wrapShipToBtn]: isShipTo
          })}
        >
          {title}{" "}
          <span style={{ color: "#222222", fontWeight: 400 }}>
            {titleInfo || ""}
          </span>
          {required && <span className={classes.required}>*</span>}
          {iconRight && value.name && (
            <SVG
              src={iconRight}
              onClick={handleOpenShipTo}
              className={classes.iconEditShipTo}
            />
          )}
        </MyTypography>

        {children}
        {getTextError()}
        {getTextWarning()}
      </div>
    </Grid>
  );

  switch (type) {
    case FIELD_TYPE.EMPTY:
      return renderChild(<></>);

    case FIELD_TYPE.DISABLED_TEXT:
      return renderChild(<TextInputDisable value={value || ""} />);

    case FIELD_TYPE.SINGLE_INPUT:
      return renderChild(
        <SingleTextField
          {...commonProps}
          onChange={handleChange}
          maxLengthProps={maxLength}
          disabled={isDisabled}
          hint={hint}
          id={id}
          testId={id}
          InputProps={{
            inputProps: { "data-testid": `${id}-input` }
          }}
        />
      );
    case FIELD_TYPE.NUMBER_INPUT:
      return renderChild(
        <NumberInput
          {...commonProps}
          onChange={handleChange}
          disabled={isDisabled}
          maxLength={maxLength}
          id={id}
        />
      );

    case FIELD_TYPE.SINGLE_SELECT:
      return renderChild(
        <SingleSelectField
          {...commonProps}
          selectProps={selectProps}
          InputProps={{
            ...InputProps,
            inputProps: {
              ...InputProps?.inputProps,
              "data-testid": `${id}-input`
            }
          }}
          className={classes.selectField}
          menuListCustomStyle={{
            overflowY: "auto"
          }}
          choices={options}
          onChange={handleChange}
          disabled={isDisabled}
          labelParser={labelParser}
          id={id}
          testId={id}
        />
      );
    case FIELD_TYPE.MULTIPLE_SELECT:
      return renderChild(
        <OrderStatus
          {...commonProps}
          testId="orderStatus-select"
          disableIndeterminate
          showChipDeleteButton={false}
          choices={multiSelectOptions}
          onChange={handleChange}
          showCircularIcon={optionsShowCircularIcon}
          isClean={isClean}
          listItemClass={classes.listItemClass}
          numberItemDisplay={2}
        />
      );
    case FIELD_TYPE.SUGGESTION_INPUT:
      return renderChild(
        <InputSuggestion
          {...mappingDataSuggestions}
          contained
          name={name}
          id={id}
          testId={id}
          disableDuplicationLoading
          withNonSelect
          searchField={"search"}
          defaultInputValue={value}
          defaultValue={{
            title: displayText || value,
            value
          }}
          isSearchIcon={isSearchIcon}
          objectSuggestion
          placeholder={placeholder}
          error={!!error}
          isClean={isClean}
          handleSelectedSearch={e => {
            if (customOnChange) {
              return customOnChange(e);
            }
            handleChange({
              target: {
                name,
                value: typeof e === "string" ? e : e.value
              }
            });
          }}
        />
      );
    case FIELD_TYPE.MULTIPLE_SUGGESTION_INPUT:
      return renderChild(
        <AutoSuggestionMultipleSelect
          emptyBoxClasses={
            name === "materialNoMaterialDescription" && classes.emptyBox
          }
          suggestBoxClasses={
            name === "materialNoMaterialDescription" && classes.suggestionBox
          }
          maxCount={name === "materialNoMaterialDescription" ? 1 : 10}
          isShowSelected={isShowSelected}
          reRender={reRender}
          headerText={headerText}
          message={message}
          keyFilterApi={keyFilterApi}
          name={name}
          placeholder={placeholder}
          {...mappingDataSuggestions}
          getByValue
          handleSelectedSearch={value => {
            handleChange({
              target: {
                name,
                value
              }
            });
          }}
          id={id}
        />
      );

    case FIELD_TYPE.RADIO_GROUP:
      return renderChild(
        <RadioGroup
          style={style}
          className={classNames(classes.radioGroup, className)}
          aria-labelledby="demo-controlled-radio-buttons-group"
          name={name}
          value={value}
          onChange={handleChange}
          id={id}
          data-testid={id}
        >
          {options?.map((o, oKey) => (
            <FormControlLabel
              key={`radio${oKey}`}
              value={o.value}
              id={`${id}-${oKey}`}
              control={<Radio />}
              label={
                typeof o.label === "string" ? (
                  <MyTypography variant={"body3"}>{o.label}</MyTypography>
                ) : (
                  o.label
                )
              }
            />
          ))}
        </RadioGroup>
      );
    case FIELD_TYPE.DATERANGE_PICKER:
      return renderChild(
        <>
          <Box display="flex" justifyItems="center" alignItems="center">
            <DatePicker
              type="gte"
              dateRange={dateRangeInDays}
              name={`${name}`}
              id={`${id}-from`}
              data-testid={`${id}-from`}
              disableManualInput={disableManualDateInput}
              value={value?.gte || ""}
              onChange={e => {
                handleChange({
                  target: {
                    name: `${name}.gte`,
                    value: e.target.value
                  }
                });
              }}
              error={!!error?.gte}
              format={dateFormat}
              maxDate={value.lte ? moment(value.lte) : undefined}
              minDate={undefined}
            />
            <Box px={1} display="flex" alignItems="center">
              <SVG src={LineIcon} width={12} height={2} />
            </Box>
            <DatePicker
              type="lte"
              dateRange={dateRangeInDays}
              name={`${name}`}
              id={`${id}-to`}
              data-testid={`${id}-from`}
              disableManualInput={disableManualDateInput}
              value={value?.lte || ""}
              error={!!error?.lte}
              onChange={e => {
                handleChange({
                  target: {
                    name: `${name}.lte`,
                    value: e.target.value
                  }
                });
              }}
              format="YYYY-MM-DD"
              maxDate={undefined}
              minDate={value.gte ? moment(value.gte) : undefined}
            />
          </Box>
          <Box className={classes.wrapDatePickerError}>
            <Box className={classes.datePickerError}>
              {getTextErrorDatePick()}
            </Box>
            <Box className={classes.empty}></Box>
            <Box className={classes.datePickerError}>
              {getTextErrorDatePick()}
            </Box>
          </Box>
        </>
      );
    case FIELD_TYPE.DATEPICKER:
      return renderChild(
        <DatePicker
          name={name}
          id={id}
          data-testid={id}
          disableFuture={disableFutureDate}
          disableManualInput={disableManualDateInput}
          value={value || ""}
          onChange={handleChange}
          format={dateFormat}
          isAllowTodayDate={isAllowTodayDate}
          maxDate={maxDate}
          minDate={minDate}
          error={!!error}
          isUpdateDate={isUpdateDate}
          disabled={isDisabled}
        />
      );

    default:
      return <div>{type + " type not supported"}</div>;
  }
};

export default FormField;
